<template>
  <div class="publication-detail">
    <nav class="breadcrumb">
      <router-link class="breadcrumb__link" to="/">{{ breadcrumb.home }}</router-link>
      <span class="breadcrumb__separator">&gt;</span>
      <router-link class="breadcrumb__link" :to="{ name: 'Publications' }">
        {{ breadcrumb.list }}
      </router-link>
      <span class="breadcrumb__separator">&gt;</span>
      <span class="breadcrumb__current">{{ breadcrumb.current }}</span>
    </nav>

    <section v-if="isReady" class="detail">
      <header class="detail__header">
        <h1 class="detail__title">{{ displayTitle }}</h1>
        <p v-if="displayAuthors" class="detail__authors">{{ displayAuthors }}</p>
        <p v-if="affiliationsText" class="detail__affiliations">{{ affiliationsText }}</p>
        <ul v-if="metaItems.length" class="detail__meta">
          <li v-for="item in metaItems" :key="item.label" class="detail__meta-item">
            <span class="detail__meta-label">{{ item.label }}</span>
            <span class="detail__meta-value">{{ item.value }}</span>
          </li>
        </ul>
      </header>

      <section v-if="abstractParagraphs.length" class="detail__section">
        <h2 class="detail__section-title heading-with-bar">{{ labels.abstract }}</h2>
        <p v-for="paragraph in abstractParagraphs" :key="paragraph" class="detail__paragraph">
          {{ paragraph }}
        </p>
      </section>

      <div v-if="links.length" class="detail__actions">
        <a
          v-for="link in links"
          :key="link.url"
          :href="link.url"
          target="_blank"
          rel="noopener"
          :class="['action-button', `action-button--${link.variant || 'primary'}`]"
        >
          {{ link.label }}
        </a>
      </div>
    </section>

    <section v-else class="detail__empty">
      <div class="empty-card">
        <div class="empty-card__icon" aria-hidden="true">!</div>
        <h1 class="detail__title">{{ emptyState.heading }}</h1>
        <p class="detail__paragraph">
          {{ emptyState.message }}
        </p>
        <div class="detail__empty-actions">
          <router-link class="action-button action-button--primary" :to="{ name: 'Publications' }">
            {{ emptyState.actions.list }}
          </router-link>
          <router-link class="action-button action-button--ghost" to="/">
            {{ emptyState.actions.home }}
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { getLocaleBlock } from '@/utils/i18n';

export default {
  name: 'PublicationDetailView',
  computed: {
    page() {
      return getLocaleBlock(this.$i18n, 'publicationsPage', {}) || {};
    },
    publicationId() {
      const id = this.$route.params?.id;
      return id == null ? '' : String(id);
    },
    publications() {
      const list = this.page.publications;
      return Array.isArray(list) ? list : [];
    },
    detailMap() {
      return this.page.details || {};
    },
    publication() {
      return this.publications.find((item) => String(item.id) === this.publicationId);
    },
    detail() {
      const key = this.publicationId;
      if (!key) {
        return null;
      }
      const map = this.detailMap;
      return map[key] || map[Number(key)] || null;
    },
    labels() {
      const defaults = {
        abstract: 'Abstract',
        affiliations: 'Affiliations',
        venue: 'Venue',
        year: 'Year',
        type: 'Type',
        resources: 'Resources',
      };
      return Object.assign(defaults, this.page.detailLabels || {});
    },
    breadcrumb() {
      const { breadcrumb = {} } = this.page;
      return {
        home: breadcrumb.home || 'Home',
        list: breadcrumb.current || 'Publications',
        current: this.displayTitle || this.publicationId || 'Publication',
      };
    },
    displayTitle() {
      return this.detail?.title || this.publication?.title || '';
    },
    displayAuthors() {
      return this.detail?.authors || this.publication?.authors || '';
    },
    affiliationsText() {
      const affiliations = this.detail?.affiliations;
      if (Array.isArray(affiliations)) {
        return affiliations.filter(Boolean).join(' · ');
      }
      if (typeof affiliations === 'string') {
        return affiliations;
      }
      return '';
    },
    metaItems() {
      const items = [];
      const venue = this.detail?.venue || this.publication?.venue;
      const year = this.detail?.year || this.publication?.year;
      const type = this.detail?.type || this.publication?.type;
      if (venue) {
        items.push({ label: this.labels.venue, value: venue });
      }
      if (year) {
        items.push({ label: this.labels.year, value: year });
      }
      if (type) {
        items.push({ label: this.labels.type, value: type });
      }
      return items;
    },
    links() {
      if (Array.isArray(this.detail?.links)) {
        return this.detail.links;
      }
      if (Array.isArray(this.publication?.links)) {
        return this.publication.links;
      }
      return [];
    },
    abstractParagraphs() {
      const abstract =
        this.detail?.abstract ||
        this.publication?.abstract ||
        this.publication?.description ||
        '';
      return abstract
        .split(/\n{2,}/)
        .map((segment) => segment.trim())
        .filter(Boolean);
    },
    emptyState() {
      const defaults = {
        heading: 'Publication not available',
        message: 'This publication may have moved or is still being prepared.',
        actions: {
          list: 'Back to Publications',
          home: 'Go Home',
        },
      };
      const block = getLocaleBlock(this.$i18n, 'publicationsPage.detailEmpty', {}) || {};
      const actions = block.actions || {};
      return {
        heading: block.heading || defaults.heading,
        message: block.message || defaults.message,
        actions: {
          list: actions.list || defaults.actions.list,
          home: actions.home || defaults.actions.home,
        },
      };
    },
    isReady() {
      return Boolean(this.publication || this.detail);
    },
  },
  watch: {
    displayTitle: {
      immediate: true,
      handler(title) {
        if (!title) {
          return;
        }
        const siteMeta = getLocaleBlock(this.$i18n, 'meta', {}) || {};
        const siteName = siteMeta.siteShort || siteMeta.siteName || '';
        document.title = siteName ? `${title} | ${siteName}` : title;
      },
    },
  },
};
</script>

<style scoped>
.publication-detail {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 0 4rem;
  color: #1d2a41;
  line-height: 1.7;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #54627a;
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
}

.breadcrumb__link {
  color: inherit;
  text-decoration: none;
  transition: color 0.2s ease;
  white-space: nowrap;
}

.breadcrumb__link:hover {
  color: #1f55bf;
}

.breadcrumb__separator {
  color: #a0a8b8;
}

.breadcrumb__current {
  color: #1d2a41;
  font-weight: 600;
  white-space: normal;
  overflow-wrap: anywhere;
}

.detail,
.detail__empty {
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
}

.detail__header {
  margin-bottom: 2.5rem;
}

.detail__title {
  font-size: 2.1rem;
  font-weight: 700;
  color: #12203a;
  margin-bottom: 1rem;
  line-height: 1.25;
}

.detail__authors {
  font-size: 1.05rem;
  color: #384867;
  margin-bottom: 0.5rem;
}

.detail__affiliations {
  font-size: 0.95rem;
  color: #5f6f8d;
  margin-bottom: 1.5rem;
}

.detail__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1.2rem;
  padding: 0;
  margin: 0;
  list-style: none;
}

.detail__meta-item {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.detail__meta-label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #7a869c;
}

.detail__meta-value {
  font-size: 0.92rem;
  color: #24344d;
  font-weight: 600;
}

.detail__section {
  margin-bottom: 2.5rem;
}

.detail__section-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #12203a;
  margin-bottom: 1rem;
}

.detail__paragraph {
  margin-bottom: 1rem;
  color: #37465f;
  font-size: 1rem;
}

.detail__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.5rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.02em;
  text-decoration: none;
  border: 1px solid transparent;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease,
    color 0.2s ease;
}

.action-button:hover {
  transform: translateY(-1px);
}

.action-button:focus-visible {
  outline: 2px solid #f0b429;
  outline-offset: 2px;
}

.action-button--primary {
  background: var(--primary-color);
  color: var(--text-color-on-primary);
  border-color: var(--primary-color);
  box-shadow: 0 12px 26px rgba(0, 74, 153, 0.24);
}

.action-button--primary:hover {
  background: var(--primary-color-dark);
  border-color: var(--primary-color-dark);
  box-shadow: 0 14px 30px rgba(0, 51, 102, 0.24);
}

.action-button--secondary {
  background: #f0b429;
  color: #2c1f00;
  border-color: #f0b429;
  box-shadow: 0 12px 26px rgba(240, 180, 41, 0.26);
}

.action-button--secondary:hover {
  background: #e5a200;
  border-color: #e5a200;
  color: #1f1400;
  box-shadow: 0 14px 30px rgba(229, 162, 0, 0.28);
}

.action-button--ghost {
  background: #fff;
  color: var(--primary-color);
  border-color: rgba(0, 74, 153, 0.35);
}

.action-button--ghost:hover {
  background: rgba(0, 74, 153, 0.08);
}

.detail__empty {
  display: flex;
  justify-content: center;
}

.empty-card {
  width: 100%;
  max-width: 520px;
  padding: 2.2rem;
  border-radius: 26px;
  background: linear-gradient(145deg, rgba(0, 74, 153, 0.05), rgba(12, 45, 87, 0.02)),
    #fff;
  border: 1px solid rgba(0, 74, 153, 0.08);
  box-shadow: 0 20px 46px rgba(12, 45, 87, 0.18);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-start;
  text-align: left;
}

.empty-card__icon {
  width: 3rem;
  height: 3rem;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: rgba(0, 74, 153, 0.12);
  color: var(--primary-color);
  font-size: 1.2rem;
}

.detail__empty-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

@media (max-width: 720px) {
  .detail__title {
    font-size: 1.8rem;
  }

  .breadcrumb {
    flex-wrap: wrap;
  }
}
</style>
